import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { caeKeys } from "@/lib/queryKeys";
import { listCaes } from "@/services/invoiceService";

export interface IssuableCfeTypes {
  /** cfeTypes ('101' | '111' | '121' | ...) with an active, non-deleted CAE. */
  issuable: Set<string>;
  /** e-Ticket (101) — gates Consumidor Final AND foreign clients (foreign → 101). */
  canEmitETicket: boolean;
  /** e-Factura (111) — gates Empresa con RUT. */
  canEmitEFactura: boolean;
  /** e-Factura Exportación (121). */
  canEmitExport: boolean;
  isLoading: boolean;
}

/**
 * Which CFE types the current tenant can actually issue, derived from its
 * registered CAEs (GET /invoices/cae). A type is issuable only when it has a
 * CAE with status 'active' (so expired / exhausted / not-yet-valid CAEs don't
 * count — emitting against those fails at DGI anyway). Used to gate the
 * invoice-type options in the create-invoice UI.
 */
export function useIssuableCfeTypes(): IssuableCfeTypes {
  const { data: caes = [], isLoading } = useQuery({
    queryKey: caeKeys.list(),
    queryFn: listCaes,
  });

  const issuable = useMemo(
    () =>
      new Set(
        caes
          .filter((c) => c.status === "active" && c.deletedAt == null)
          .map((c) => c.cfeType),
      ),
    [caes],
  );

  return {
    issuable,
    canEmitETicket: issuable.has("101"),
    canEmitEFactura: issuable.has("111"),
    canEmitExport: issuable.has("121"),
    isLoading,
  };
}
